// ComunicacionesPanel.jsx · "Comunicaciones" en la Cuenta (#1683, mockup elegido 1A:
// tools/mockups/smartdx_mockups_1683_derechos.html, bloque 1 opción A: dos tarjetas con
// interruptor y el recuadro de lo que llega siempre).
//
// Qué hace: muestra y cambia los dos permisos de la persona: correos de seguimiento y WhatsApp.
// El estado sale de `/api/user/me/` (bloque `comunicaciones`) y cada cambio es un
// `PATCH /api/user/me/comunicaciones/`.
//
// Reglas que no son cosméticas:
//  - El interruptor muestra lo que CONFIRMA el servidor (R49): se mueve al tiro para que se sienta
//    inmediato, pero al volver el PATCH se pinta la respuesta, y si falla vuelve al estado anterior y
//    aparece `comunicaciones-error`. Nunca queda mostrando un permiso que el servidor no registró.
//  - Sin celular canónico el interruptor de WhatsApp queda deshabilitado (R50) y el texto manda a
//    guardar el celular. Una cuenta que YA tiene el permiso y perdió el celular puede igual
//    apagarlo: no se le niega la salida (el backend lo permite, solo exige celular para ENCENDER).
//  - 44 px de alto en el área tocable (R60): el interruptor visible mide 26 px como en el mockup,
//    el `label` que lo envuelve es el objetivo táctil.
// Todo color por tokens de theme.js (R61), nada de hex.
// Cubre R48, R49, R50, R60, R61 — .claude/specs/1683-derechos-usuario/
import React, { useEffect, useState } from "react";
import { Box, Stack, Switch, Typography } from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { codigoDeError, patchComunicaciones } from "../../helpers/comunicaciones-api";

const leer = (c) => ({
  correo_seguimiento: c?.correo_seguimiento !== false, // sin dato = activado (D3)
  whatsapp: c?.whatsapp === true,
  whatsapp_disponible: c?.whatsapp_disponible === true,
  telefono_enmascarado: c?.telefono_enmascarado || "",
});

export const MENSAJE_SIN_CELULAR = "Para recibir WhatsApp primero guarda tu celular en tu perfil.";
export const MENSAJE_FALLO = "No pudimos guardar el cambio. Revisa tu conexión y vuelve a intentar.";

// Mismo marco que el Panel de ProfileView (no se importa de ahí: ProfileView importa este archivo).
const marcoSx = {
  backgroundColor: "var(--bg-elevated)",
  borderRadius: 3,
  border: "1px solid rgba(var(--fg-rgb),0.08)",
  p: { xs: 2.5, sm: 3 },
};

const iconoSx = {
  width: 34,
  height: 34,
  borderRadius: "10px",
  backgroundColor: "rgba(var(--color-primary-rgb),0.14)",
  color: "var(--color-primary)",
  display: "grid",
  placeItems: "center",
  flex: "none",
  "& svg": { fontSize: 18 },
};

const switchSx = {
  width: 44,
  height: 26,
  p: 0,
  overflow: "visible",
  "& .MuiSwitch-switchBase": {
    p: "3px",
    "&.Mui-checked": {
      transform: "translateX(18px)",
      color: "var(--text-on-primary)",
      "& + .MuiSwitch-track": { backgroundColor: "var(--color-primary)", opacity: 1 },
    },
    "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.38 },
    "&.Mui-focusVisible + .MuiSwitch-track": {
      outline: "2px solid var(--color-primary-soft)",
      outlineOffset: 2,
    },
  },
  "& .MuiSwitch-thumb": {
    width: 20,
    height: 20,
    boxShadow: "0 1px 3px rgba(var(--shadow-rgb),0.35)",
    backgroundColor: "var(--text-on-primary)",
  },
  "& .MuiSwitch-track": {
    borderRadius: 999,
    backgroundColor: "rgba(var(--fg-rgb),0.22)",
    opacity: 1,
  },
};

export const Interruptor = ({ checked, disabled, onChange, label, testid }) => (
  <Box
    component="label"
    data-testid={`${testid}-tap`}
    sx={{
      minWidth: 44,
      minHeight: 44,
      display: "grid",
      placeItems: "center",
      flex: "none",
      cursor: disabled ? "not-allowed" : "pointer",
    }}
  >
    <Switch
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      disableRipple
      sx={switchSx}
      inputProps={{ "aria-label": label, "data-testid": testid, role: "switch" }}
    />
  </Box>
);

const Tarjeta = ({ icono, titulo, texto, interruptor, estado, pie, testid }) => (
  <Box
    data-testid={`${testid}-tarjeta`}
    sx={{
      border: "1px solid rgba(var(--fg-rgb),0.08)",
      borderRadius: "14px",
      p: 2,
      backgroundColor: "rgba(var(--fg-rgb),0.025)",
      display: "flex",
      flexDirection: "column",
      gap: 1.25,
      minWidth: 0,
    }}
  >
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
      <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
        <Box sx={iconoSx}>{icono}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h3" sx={{ fontSize: 14.5, fontWeight: 700, lineHeight: 1.35 }}>
            {titulo}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "var(--text-muted)", mt: 0.25 }}>{texto}</Typography>
        </Box>
      </Stack>
      {interruptor}
    </Stack>
    <Box
      sx={{
        borderTop: "1px solid rgba(var(--fg-rgb),0.08)",
        pt: 1.1,
        fontSize: 12,
        color: "var(--text-faint)",
      }}
    >
      <Box
        component="span"
        data-testid={`${testid}-estado`}
        sx={{ fontWeight: 600, color: estado.activo ? "var(--color-success)" : "var(--text-faint)" }}
      >
        {estado.texto}
      </Box>
      {" · "}
      {pie}
    </Box>
  </Box>
);

const ComunicacionesPanel = ({ comunicaciones, onCambio }) => {
  const [estado, setEstado] = useState(() => leer(comunicaciones));
  const [enVuelo, setEnVuelo] = useState(false);
  const [error, setError] = useState("");

  // Si /me se refresca desde afuera, el panel sigue a la fuente.
  useEffect(() => {
    setEstado(leer(comunicaciones));
  }, [
    comunicaciones?.correo_seguimiento,
    comunicaciones?.whatsapp,
    comunicaciones?.whatsapp_disponible,
    comunicaciones?.telefono_enmascarado,
  ]);

  const cambiar = async (canal, valor) => {
    if (enVuelo) return;
    const previo = estado;
    setError("");
    setEnVuelo(true);
    setEstado({ ...estado, [canal]: valor });
    try {
      const confirmado = await patchComunicaciones({ [canal]: valor });
      setEstado(leer(confirmado));
      if (onCambio) onCambio(confirmado);
    } catch (e) {
      setEstado(previo);
      setError(codigoDeError(e) === "sin_celular" ? MENSAJE_SIN_CELULAR : MENSAJE_FALLO);
    } finally {
      setEnVuelo(false);
    }
  };

  const wa = estado.whatsapp;
  const sinCelular = !estado.whatsapp_disponible;

  return (
    <Box data-testid="comunicaciones-panel" sx={marcoSx}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 0.5 }}>
        <Box sx={{ ...iconoSx, width: 32, height: 32, borderRadius: "9px" }}>
          <MailOutlineIcon />
        </Box>
        <Typography component="h2" sx={{ fontSize: 15, fontWeight: 700 }}>
          Comunicaciones
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 13, color: "var(--text-muted)", mb: 1.75 }}>
        Tú decides qué mensajes de seguimiento quieres recibir. Puedes cambiarlo cuando quieras.
      </Typography>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "1fr 1fr" }, gap: 1.5 }}>
        <Tarjeta
          testid="comunicaciones-correo"
          icono={<MailOutlineIcon />}
          titulo="Correos de seguimiento y novedades"
          texto="Ayuda para empezar, recordatorios de lo que dejaste a medias y novedades de SmartDx."
          estado={{ activo: estado.correo_seguimiento, texto: estado.correo_seguimiento ? "Activado" : "Desactivado" }}
          pie="Cada correo trae un link para dejar de recibirlos."
          interruptor={
            <Interruptor
              testid="comunicaciones-correo"
              label="Correos de seguimiento y novedades"
              checked={estado.correo_seguimiento}
              disabled={enVuelo}
              onChange={(v) => cambiar("correo_seguimiento", v)}
            />
          }
        />
        <Tarjeta
          testid="comunicaciones-whatsapp"
          icono={<WhatsAppIcon />}
          titulo="Mensajes por WhatsApp"
          texto={
            estado.telefono_enmascarado
              ? `Te escribimos solo para ayudarte con SmartDx. Al número ${estado.telefono_enmascarado}.`
              : "Te escribimos solo para ayudarte con SmartDx."
          }
          estado={{ activo: wa, texto: wa ? "Activado" : "Desactivado" }}
          pie={
            sinCelular
              ? "Para activarlo, guarda tu celular en tu perfil."
              : wa
                ? "Puedes responder BAJA en cualquier mensaje para salir."
                : "Si lo activas, puedes responder BAJA en cualquier mensaje para salir."
          }
          interruptor={
            <Interruptor
              testid="comunicaciones-whatsapp"
              label="Mensajes por WhatsApp"
              checked={wa}
              disabled={enVuelo || (sinCelular && !wa)}
              onChange={(v) => cambiar("whatsapp", v)}
            />
          }
        />
      </Box>

      {error && (
        <Typography
          role="alert"
          data-testid="comunicaciones-error"
          sx={{ mt: 1.5, fontSize: 13, fontWeight: 600, color: "var(--color-danger)" }}
        >
          {error}
        </Typography>
      )}

      <Box
        data-testid="comunicaciones-siempre"
        sx={{
          display: "flex",
          gap: 1.25,
          mt: 1.75,
          p: "11px 13px",
          borderRadius: "10px",
          fontSize: 12.5,
          color: "var(--text-tertiary)",
          backgroundColor: "rgba(var(--fg-rgb),0.04)",
          border: "1px solid rgba(var(--fg-rgb),0.08)",
        }}
      >
        <ShieldOutlinedIcon sx={{ fontSize: 16, mt: "2px", flex: "none", color: "var(--text-muted)" }} />
        <Box>
          <b style={{ color: "var(--text-primary)" }}>Esto te llega siempre, aunque apagues todo:</b>{" "}
          avisos de seguridad de tu cuenta, el correo para recuperar tu clave, y los avisos de cobro,
          vencimiento o suspensión de tu suscripción.
        </Box>
      </Box>
    </Box>
  );
};

export default ComunicacionesPanel;
